import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  UploadCloud,
  FileText,
  X,
  Loader2,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const UploadResumesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [job, setJob] = useState(null);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);

  useDocumentTitle(
    job ? `Upload de CVs — ${job.title}` : "Upload de currículos",
  );

  useEffect(() => {
    axios.get(`${API}/jobs/${id}`).then((res) => setJob(res.data)).catch(() => {
      toast.error("Vaga não encontrada");
      navigate("/vagas");
    });
  }, [id, navigate]);

  const onSelectFiles = (selected) => {
    const arr = Array.from(selected || []);
    const accepted = arr.filter((f) => {
      const n = f.name.toLowerCase();
      return n.endsWith(".pdf") || n.endsWith(".zip");
    });
    const skipped = arr.length - accepted.length;
    if (skipped > 0) toast.warning(`${skipped} arquivo(s) ignorado(s) — apenas PDF ou ZIP`);
    if (accepted.length === 0) return;

    setFiles((prev) => {
      const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const fresh = accepted.filter((f) => !existing.has(`${f.name}-${f.size}`));
      return [...prev, ...fresh];
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    onSelectFiles(e.dataTransfer.files);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error("Selecione pelo menos um PDF");
      return;
    }
    setUploading(true);
    setResults([]);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f, f.name));
      const res = await axios.post(`${API}/jobs/${id}/candidates/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const count = res.data?.length || 0;
      setFiles([]);
      toast.success(
        `${count} currículo(s) recebido(s). A IA está analisando em segundo plano...`
      );
      // Redirect to job detail page where the pending banner + auto-polling
      // will show the analysis progress in real time.
      navigate(`/vagas/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar currículos");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="upload-page" className="space-y-8">
      <Link
        to={`/vagas/${id}`}
        data-testid="back-to-job"
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar para a vaga
      </Link>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
          Upload de currículos
        </div>
        <h1 className="heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">
          Enviar PDFs para análise
        </h1>
        {job && (
          <p className="text-sm text-slate-600 mt-2">
            Vaga: <span className="font-medium text-slate-900">{job.title}</span>{" "}
            {job.department && <span>· {job.department}</span>}
          </p>
        )}
      </div>

      {/* Dropzone */}
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
          dragOver ? "dropzone-active" : "border-slate-300 hover:bg-slate-50 hover:border-slate-400 bg-white"
        }`}
      >
        <input
          ref={inputRef}
          data-testid="upload-file-input"
          type="file"
          multiple
          accept="application/pdf,.pdf,application/zip,.zip"
          className="hidden"
          onChange={(e) => onSelectFiles(e.target.files)}
        />
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-brand-gradient flex items-center justify-center shadow-md">
            <UploadCloud className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="heading text-lg font-semibold text-slate-900">
              Arraste PDFs ou um arquivo ZIP aqui
            </div>
            <div className="text-sm text-slate-600 mt-1">
              ou clique para selecionar · até 50 PDFs por upload (10 MB cada)
            </div>
            <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
              💡 Baixou um ZIP com vários currículos da Catho/InfoJobs? Solte ele aqui — extraímos os PDFs automaticamente.
            </div>
          </div>
        </div>
      </div>

      {/* Selected files */}
      {files.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-medium text-slate-900">
              {files.length} arquivo(s) selecionado(s)
            </div>
            <Button
              data-testid="upload-submit"
              onClick={handleUpload}
              disabled={uploading}
              className="bg-brand-blue hover:bg-brand-blue-dark text-white"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analisando com IA...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Analisar com IA
                </>
              )}
            </Button>
          </div>
          <ul className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {files.map((f, i) => {
              const isZip = f.name.toLowerCase().endsWith(".zip");
              return (
                <li
                  key={`${f.name}-${i}`}
                  data-testid={`upload-file-row-${i}`}
                  className="flex items-center justify-between px-5 py-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-9 h-9 rounded-md flex items-center justify-center ${
                        isZip
                          ? "bg-amber-50 border border-amber-200 text-amber-700"
                          : "bg-red-50 border border-red-100 text-red-600"
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {f.name}{" "}
                        {isZip && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wider font-semibold text-amber-700">
                            ZIP
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{formatBytes(f.size)}</div>
                    </div>
                  </div>
                  <button
                    data-testid={`remove-file-${i}`}
                    onClick={() => removeFile(i)}
                    disabled={uploading}
                    className="text-slate-400 hover:text-red-600 p-1.5 rounded hover:bg-slate-100"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {uploading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 flex items-start gap-3">
          <Loader2 className="w-5 h-5 text-blue-600 animate-spin shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-blue-900">
              IA analisando currículos em paralelo
            </div>
            <div className="text-xs text-blue-800 mt-0.5">
              Isso pode levar alguns segundos por currículo. Você pode acompanhar os
              resultados ao lado quando terminar.
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="heading text-base font-semibold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                {results.length} candidato(s) processado(s)
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Visualize o score completo na tela da vaga.
              </p>
            </div>
            <Link
              to={`/vagas/${id}`}
              data-testid="view-score-button"
              className="bg-brand-blue hover:bg-brand-blue-dark text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Ver score completo
            </Link>
          </div>
          <ul className="divide-y divide-slate-100">
            {results.map((c) => (
              <li
                key={c.id}
                data-testid={`result-row-${c.id}`}
                className="px-5 py-3 flex items-center justify-between"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">
                    {c.name || c.filename}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.email || "sem e-mail"} · {c.phone || "sem telefone"}
                  </div>
                </div>
                <div className="text-right">
                  <span
                    className={`heading text-lg font-bold tabular-nums ${
                      c.score >= 75
                        ? "text-emerald-600"
                        : c.score >= 50
                        ? "text-amber-600"
                        : "text-red-600"
                    }`}
                  >
                    {c.score}%
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UploadResumesPage;
